import React from 'react';
import { Text } from '@/components/ui';

/**
 * Highlights matching parts between filename and form name for auto-matched forms
 */
export const highlightMatchingParts = (
  fileName: string,
  formName: string,
  isAutoMatched: boolean
): React.ReactNode => {
  if (!isAutoMatched) {
    return (
      <Text size="3" weight="bold">
        {formName}
      </Text>
    );
  }

  // Clean filename (same logic as auto-matching)
  const cleanFileName = fileName
    .toLowerCase()
    .replace(/\.csv$/i, '')
    .replace(/^[a-z0-9]+_/i, '')
    .replace(/_\d+[a-z]+\d+$/i, '')
    .replace(/_v\d+$/i, '')
    .replace(/_\d+$/i, '');

  const lowerFormName = formName.toLowerCase();

  // Find matching words to highlight
  const fileWords = cleanFileName.split('_');
  const formNameParts = formName.split('_');

  // Find which words in the form name match file words
  const highlightedParts: Array<{ text: string; highlight: boolean }> = [];

  formNameParts.forEach(part => {
    const isMatching = fileWords.includes(part.toLowerCase());
    highlightedParts.push({
      text: part,
      highlight: isMatching,
    });
  });

  // If no word matches found, try substring matching
  if (!highlightedParts.some(p => p.highlight)) {
    if (lowerFormName.includes(cleanFileName)) {
      const start = lowerFormName.indexOf(cleanFileName);
      const end = start + cleanFileName.length;

      return (
        <Text size="3" weight="bold">
          {formName.substring(0, start)}
          <span
            style={{
              backgroundColor: 'var(--yellow-4)',
              color: 'var(--yellow-11)',
              padding: '1px 2px',
              borderRadius: '2px',
              fontWeight: 'bold',
            }}
          >
            {formName.substring(start, end)}
          </span>
          {formName.substring(end)}
        </Text>
      );
    }

    // No matches found, return normal text
    return (
      <Text size="3" weight="bold">
        {formName}
      </Text>
    );
  }

  // Render with highlighted words
  return (
    <Text size="3" weight="bold">
      {highlightedParts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && '_'}
          {part.highlight ? (
            <span
              style={{
                backgroundColor: 'var(--yellow-4)',
                color: 'var(--yellow-11)',
                padding: '1px 2px',
                borderRadius: '2px',
                fontWeight: 'bold',
              }}
            >
              {part.text}
            </span>
          ) : (
            part.text
          )}
        </React.Fragment>
      ))}
    </Text>
  );
};

/**
 * Get clean filename for comparison (used in auto-matching)
 */
export const getCleanFileName = (fileName: string): string => {
  return fileName
    .toLowerCase()
    .replace(/\.csv$/, '')
    .replace(/^[a-z0-9]+_/, '') // Remove prefixes
    .replace(/_\d+[a-z]+\d+$/, '') // Remove date suffixes
    .replace(/_v\d+$/, '') // Remove version suffixes
    .replace(/_\d+$/, ''); // Remove numeric suffixes
};
